import {Injectable, Injector, Signal, Type, inject, signal} from '@angular/core';

import {DrawerConfig} from './drawer-config.model';
import {DrawerRef} from './drawer-ref';

export interface DrawerState<TResult = unknown, TData = unknown> {
  readonly ref: DrawerRef<TResult, TData>;
  readonly componentType: Type<unknown>;
  readonly config: DrawerConfig<TData>;
  readonly injector: Injector;
}

@Injectable({providedIn: 'root'})
export class DrawerService {
  private readonly parentInjector = inject(Injector);
  private readonly _state = signal<DrawerState | null>(null);

  readonly state: Signal<DrawerState | null> = this._state.asReadonly();

  open<TComponent, TResult = unknown, TData = unknown>(
    component: Type<TComponent>,
    config: DrawerConfig<TData> = {},
  ): DrawerRef<TResult, TData> {
    const current = this._state();
    if (current) current.ref.close();

    const ref = new DrawerRef<TResult, TData>(config.data, config.title);

    const injector = Injector.create({
      parent: this.parentInjector,
      providers: [{provide: DrawerRef, useValue: ref}],
    });

    const state: DrawerState<TResult, TData> = {
      ref,
      componentType: component,
      config,
      injector,
    };

    this._state.set(state as DrawerState);

    return ref;
  }

  closeActive(result?: unknown): void {
    this._state()?.ref.close(result);
  }

  /** Host: remove the drawer from the DOM once its leave animation has finished. */
  destroyActive(): void {
    this._state.set(null);
  }
}
